import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { ArrowRight, Eye, Play } from 'lucide-react';

const isVideoLike = (article) => article?.category === 'Cincy Worldstar' || article?.post_type === 'video';

export const CategoryPreviewRow = ({ title, articles = [], viewAllPath, testId }) => {
  if (!articles.length) return null;

  return (
    <section className="max-w-6xl mx-auto px-2 sm:px-8 py-8 sm:py-12 border-b border-[#1f1f1f]" data-testid={testId}>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between mb-7 group/title">
        <h2
          className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase text-transparent transition-[text-shadow] duration-300"
          style={{ WebkitTextStroke: '1.5px #f9fafb', textShadow: '0 0 20px rgba(202, 138, 4, 0.2)' }}
          data-testid={`${testId}-title`}
        >
          {title}
        </h2>
        <Link
          to={viewAllPath}
          className="inline-flex items-center gap-2 text-sm font-bold text-gray-400 hover:text-yellow-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-yellow-500 transition-colors"
          data-testid={`${testId}-view-all`}
        >
          View all <ArrowRight size={14} />
        </Link>
      </div>

      <div className="grid grid-cols-3 gap-1.5 sm:gap-3 lg:gap-4" data-testid={`${testId}-grid`}>
        {articles.slice(0, 3).map((article, index) => {
          const articleUrl = `/article/${article.slug || article.id}`;
          const isVideo = isVideoLike(article);

          return (
            <Link
              key={article.id}
              to={articleUrl}
              className={`group block h-full animate-fade-in-up stagger-${index + 1}`}
              data-testid={`${testId}-card-${article.id}`}
            >
              <article className="h-full overflow-hidden border border-[#262626] bg-[#0d0d0d] transition-[transform,border-color,box-shadow] duration-300 group-hover:-translate-y-1 group-hover:border-yellow-600/60 group-hover:shadow-[0_12px_35px_rgba(202,138,4,0.12)]">
                <div className="relative aspect-[4/3] overflow-hidden bg-[#151515]">
                  {article.featured_image ? (
                    <img
                      src={article.featured_image}
                      alt={article.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#240606] to-[#090909] text-yellow-600 text-4xl font-black">C</div>
                  )}
                  {isVideo && (
                    <span className="absolute right-1 top-1 inline-flex items-center gap-0.5 bg-black/75 px-1 py-0.5 text-[7px] font-bold uppercase text-white sm:right-2 sm:top-2 sm:px-2 sm:text-[9px]" data-testid={`${testId}-video-badge-${article.id}`}>
                      <Play size={8} fill="currentColor" className="text-yellow-500" /> Video
                    </span>
                  )}
                </div>
                <div className="p-1.5 sm:p-3 lg:p-4">
                  <h3 className="text-[10px] sm:text-xs md:text-sm lg:text-base font-black leading-tight text-white line-clamp-2 group-hover:text-yellow-500 transition-colors" data-testid={`${testId}-card-title-${article.id}`}>
                    {article.title}
                  </h3>
                  <div className="mt-1.5 flex items-center justify-between gap-1 text-[8px] text-gray-500 sm:mt-2 sm:text-[10px] lg:text-xs" data-testid={`${testId}-card-meta-${article.id}`}>
                    <span>{article.published_at ? format(new Date(article.published_at), 'MMM d, yyyy') : 'Draft'}</span>
                    <span className="inline-flex items-center gap-1 text-green-600 font-medium">
                      <Eye size={8} /> {article.views || 0}
                    </span>
                  </div>
                </div>
              </article>
            </Link>
          );
        })}
      </div>
    </section>
  );
};